import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress, makeStyles, Tooltip } from '@material-ui/core'
import clsx from 'clsx'
import Icon from '../../../../components/atoms/Icon'
import { formatFileSize } from '../utils/attachmentUtils'

const useStyles = makeStyles(() => ({
  imagePreview: {
    position: 'relative',
    width: '58px',
    height: '58px',
    flexShrink: 0,
    '&:hover': {
      '& $removeButton': {
        opacity: 1
      }
    }
  },
  imageInner: {
    position: 'relative',
    width: '54px',
    height: '54px',
    borderRadius: 'var(--border-radius-md)',
    overflow: 'hidden',
    border: '2px solid var(--dai-color-border-accent)'
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  imageOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  // File chip styles
  fileChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: 'var(--dai-color-surface-d1)',
    borderRadius: 'var(--border-radius-md)',
    border: '2px solid var(--dai-color-border-accent)',
    maxWidth: '220px',
    position: 'relative',
    '&:hover': {
      '& $removeButton': {
        opacity: 1
      }
    }
  },
  fileIcon: {
    flexShrink: 0,
    color: 'var(--dai-color-text-dim)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  fileInfo: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    flex: 1,
    minWidth: 0
  },
  fileName: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--dai-color-text)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  textSnippet: {
    fontSize: '12px',
    color: 'var(--dai-color-text)',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    boxOrient: 'vertical',
    lineHeight: 1.3,
    fontStyle: 'italic'
  },
  fileSize: {
    fontSize: '11px',
    color: 'var(--dai-color-text-dim)'
  },
  // Remove button
  removeButton: {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-neutral-700)',
    color: 'var(--color-neutral-100)',
    border: '2px solid var(--dai-color-input)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 150ms ease',
    zIndex: 10,
    '&:hover': {
      backgroundColor: 'var(--color-neutral-800)'
    },
    '& svg': {
      fontSize: '10px'
    }
  },
  // Uploading state
  uploading: {
    opacity: 0.7
  },
  // Error state
  error: {
    borderColor: 'var(--color-danger-500)',
    border: '1px solid var(--color-danger-500)'
  },
  errorIcon: {
    color: 'var(--color-danger-500)'
  },
  errorText: {
    fontSize: '11px',
    color: 'var(--color-danger-500)'
  },
  retryButton: {
    padding: '2px 6px',
    fontSize: '11px',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-danger-500)',
    borderRadius: '4px',
    color: 'var(--color-danger-500)',
    cursor: 'pointer',
    marginLeft: 'auto',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: 'var(--color-danger-100)'
    }
  },
  errorOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    cursor: 'pointer'
  },
  errorOverlayIcon: {
    color: 'var(--color-danger-400)',
    fontSize: '24px'
  },
  errorOverlayText: {
    color: '#fff',
    fontSize: '10px',
    fontWeight: 500
  },
  textPreviewTooltip: {
    maxWidth: '350px',
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    fontSize: '11px',
    lineHeight: 1.4
  }
}))

// Get icon name based on content type for user-uploaded files
function getFileIcon (contentType) {
  if (!contentType) return 'file'

  if (contentType.startsWith('image/')) return 'image'
  if (contentType === 'application/pdf') return 'document'
  if (contentType.includes('json')) return 'database'
  if (contentType.includes('xml')) return 'database'
  if (contentType.startsWith('text/')) return 'document'

  return 'file'
}

function AttachmentPreview ({ attachment, onRemove, onRetry, disabled }) {
  const classes = useStyles()
  const [imageUrl, setImageUrl] = useState(null)
  const [textPreview, setTextPreview] = useState(null)

  const isImage = attachment.contentType?.startsWith('image/')
  const isText = attachment.contentType?.startsWith('text/') ||
    attachment.contentType === 'application/json' ||
    attachment.contentType === 'application/xml'
  const isUploading = attachment.status === 'uploading'
  const isError = attachment.status === 'error'

  // Create object URL for image preview
  useEffect(() => {
    if (isImage && attachment.file) {
      const url = URL.createObjectURL(attachment.file)
      setImageUrl(url)

      // Cleanup on unmount
      return () => {
        URL.revokeObjectURL(url)
      }
    }
  }, [isImage, attachment.file])

  // Read text content for preview tooltip
  useEffect(() => {
    let cancelled = false

    if (isText && attachment.file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        if (cancelled) return
        const content = e.target.result
        // Truncate to first 500 chars for tooltip
        const preview = content.length > 500
          ? content.substring(0, 500) + '...'
          : content
        setTextPreview(preview)
      }
      reader.readAsText(attachment.file)

      return () => {
        cancelled = true
        reader.abort()
      }
    } else if (attachment.textContent) {
      // For text attachments created from selected text
      const preview = attachment.textContent.length > 500
        ? attachment.textContent.substring(0, 500) + '...'
        : attachment.textContent
      setTextPreview(preview)
    }
  }, [isText, attachment.file, attachment.textContent])

  const fileSize = attachment.file?.size
    ? formatFileSize(attachment.file.size)
    : null

  const handleRemove = useCallback((e) => {
    e.stopPropagation()
    if (!disabled && !isUploading) {
      onRemove()
    }
  }, [disabled, isUploading, onRemove])

  const handleRetry = useCallback((e) => {
    e.stopPropagation()
    if (!disabled && onRetry) {
      onRetry()
    }
  }, [disabled, onRetry])

  // Image preview
  if (isImage && imageUrl) {
    return (
      <Tooltip title={attachment.fileName} arrow placement='top'>
        <div className={clsx(classes.imagePreview, {
          [classes.uploading]: isUploading,
          [classes.error]: isError
        })}
        >
          <div className={classes.imageInner}>
            <img
              src={imageUrl}
              alt={attachment.fileName}
              className={classes.image}
            />
            {isUploading ? (
              <div className={classes.imageOverlay}>
                <CircularProgress size={24} style={{ color: '#fff' }} />
              </div>
            ) : null}
            {isError ? (
              <Tooltip title='Click to retry upload' arrow>
                <div className={classes.errorOverlay} onClick={handleRetry}>
                  <Icon name='warning' className={classes.errorOverlayIcon} />
                  <span className={classes.errorOverlayText}>Failed</span>
                </div>
              </Tooltip>
            ) : null}
          </div>
          {!isUploading ? (
            <button
              type='button'
              className={classes.removeButton}
              onClick={handleRemove}
              disabled={disabled}
              aria-label={`Remove ${attachment.fileName}`}
            >
              <Icon name='close' />
            </button>
          ) : null}
        </div>
      </Tooltip>
    )
  }

  // File chip for non-images
  const tooltipContent = textPreview ? (
    <div className={classes.textPreviewTooltip}>
      <div style={{ fontWeight: 500, marginBottom: '4px' }}>{attachment.fileName}</div>
      {textPreview}
    </div>
  ) : attachment.fileName

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement='top'
      interactive={!!textPreview}
      enterDelay={textPreview ? 300 : 500}
    >
      <div className={clsx(classes.fileChip, {
        [classes.uploading]: isUploading,
        [classes.error]: isError
      })}
      >
        <div className={classes.fileIcon}>
          {isUploading ? (
            <CircularProgress size={18} style={{ color: 'var(--dai-color-text-dim)' }} />
          ) : isError ? (
            <Icon name='warning' className={classes.errorIcon} style={{ fontSize: '18px' }} />
          ) : (
            <Icon name={getFileIcon(attachment.contentType)} style={{ fontSize: '18px' }} />
          )}
        </div>
        <div className={classes.fileInfo}>
          {attachment.textContent ? (
            <span className={classes.textSnippet}>
              {attachment.textContent}
            </span>
          ) : (
            <>
              <span className={classes.fileName}>
                {attachment.fileName}
              </span>
              {isError ? (
                <span className={classes.errorText}>Upload failed</span>
              ) : fileSize ? (
                <span className={classes.fileSize}>{fileSize}</span>
              ) : null}
            </>
          )}
        </div>
        {isError && onRetry ? (
          <button
            type='button'
            className={classes.retryButton}
            onClick={handleRetry}
            disabled={disabled}
          >
            Retry
          </button>
        ) : null}
        {!isUploading ? (
          <button
            type='button'
            className={classes.removeButton}
            onClick={handleRemove}
            disabled={disabled}
            aria-label={`Remove ${attachment.fileName}`}
          >
            <Icon name='close' />
          </button>
        ) : null}
      </div>
    </Tooltip>
  )
}

AttachmentPreview.propTypes = {
  attachment: PropTypes.shape({
    file: PropTypes.instanceOf(File),
    status: PropTypes.oneOf(['uploading', 'uploaded', 'error']).isRequired,
    fileName: PropTypes.string.isRequired,
    contentType: PropTypes.string,
    s3Key: PropTypes.string,
    textContent: PropTypes.string // For attachments created from selected text
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  onRetry: PropTypes.func,
  disabled: PropTypes.bool
}

AttachmentPreview.defaultProps = {
  disabled: false,
  onRetry: null
}

export default AttachmentPreview
